import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import i18n from 'i18n-js';

import { Box, Grid, Paper, Container, Typography } from '@material-ui/core';

import { useQuery } from 'hooks';
import moment from 'moment';
import { buildUrl, getRecurringPayment, toQueryString } from 'utils';
import { SubscriptionsActions } from 'state/modules/subscriptions';
import { useStyles, tableStyles } from './List.styled';
import { Table } from 'components/UI';
import { useHistory } from 'react-router-dom';
import { UsersSelect } from 'components/AsyncFields';
import { VehiclesSelect } from 'components/AsyncFields';
import { StatusDetailsSelect, SubscriptionStatusSelect } from 'components/DataFields';
import { debounce } from 'lodash';

const SubscriptionList = () => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const history = useHistory();
  const urlParams = useQuery();
  const loading = useSelector((state) => state.subscriptions.loading);
  const subscriptions = useSelector((state) => state.subscriptions.subscriptions);
  const pagination = useSelector((state) => state.subscriptions.pagination);
  const localeFormat = useSelector((state) => state.organizations.localeFormat);
  const isMounted = useRef(false);
  const abortController = useRef(null);

  const [params, setParams] = useState({
    page: 1,
    limit: 10,
    ...urlParams,
  });

  const columns = [
    {
      name: i18n.t('tableColumns.id'),
      selector: 'id',
      sortable: true,
      grow: 0,
    },
    {
      name: i18n.t('tableColumns.customer'),
      selector: 'user_id',
      cell: (row) =>
        row?.user ? row?.user?.first_name + ' ' + (row?.user?.last_name || '') + ' ' : '--',
      sortable: true,
      grow: 1,
    },
    {
      name: i18n.t('tableColumns.vehicle'),
      selector: 'vehicle_id',
      cell: (row) => row?.vehicle?.name || '--',
      grow: 1,
      sortable: true,
    },
    {
      name: i18n.t('tableColumns.recurring_payment'),
      selector: (row) => getRecurringPayment(row?.pricing),
      grow: 2,
    },
    {
      name: i18n.t('tableColumns.next_payment'),
      selector: (row) => {
        return row.next_start_time && moment(row.next_start_time).isValid() ? (
          <DatetimeField datetime={row?.next_start_time} />
        ) : (
          '--'
        );
      },
      grow: 6,
    },
    {
      name: i18n.t('tableColumns.status'),
      grow: 3,
      minWidth: '140px',
      selector: (row) =>
        row?.status ? (
          <div className={`${classes.status} ${classes[row?.status.toLowerCase()]}`}>
            <span className="status">
              {i18n.t(`subscriptions.statuses.${row?.status}`, {
                defaultValue: row?.status,
              })}
            </span>
          </div>
        ) : (
          '--'
        ),
    },
    {
      name: i18n.t('tableColumns.details'),
      selector: (row) => {
        let details = '';
        if (row.status_details && row.status_details.length) {
          details = row.status_details.map((value) => {
            const status = i18n.t(`subscriptions.status_details.${value.toLowerCase()}`) || value;
            return status;
          });
        }

        return (
          <Typography component="div" className={classes.wrap} onClick={() => handleRowClick(row)}>
            <Box fontSize={13}>
              {row.status !== 'INPROGRESS' ? (details ? details.join(', ') : '--') : '--'}
            </Box>
          </Typography>
        );
      },
      grow: 2,
    },
    {
      name: i18n.t('tableColumns.start_time'),
      selector: 'start_time',
      cell: (row) =>
        row.start_used_time && moment(row.start_used_time).isValid() ? (
          <DatetimeField datetime={row?.start_used_time} />
        ) : row.start_reserved_time && moment(row.start_reserved_time).isValid() ? (
          <DatetimeField datetime={row?.start_reserved_time} />
        ) : (
          '--'
        ),
      grow: 4,
    },
    {
      name: i18n.t('tableColumns.end_time'),
      selector: 'end_used_time',
      cell: (row) =>
        row.end_used_time && moment(row.end_used_time).isValid() ? (
          <DatetimeField datetime={row?.end_used_time} />
        ) : row.end_reserved_time && moment(row.end_reserved_time).isValid() ? (
          <DatetimeField datetime={row?.end_reserved_time} />
        ) : (
          '--'
        ),
      grow: 4,
    },
  ];

  const fetchSubscriptions = useCallback(
    (params = {}) => {
      // Abort previous if any request
      abortController?.current?.abort();
      // Create new abort controller and send signal
      abortController.current = new AbortController();
      dispatch(
        SubscriptionsActions.fetchSubscriptions(params, {
          signal: abortController?.current?.signal,
        })
      );
    },
    [dispatch]
  );

  const fetchSubscriptionsDebounced = useCallback(
    debounce((params) => fetchSubscriptions(params), 500),
    [fetchSubscriptions]
  );

  // On params change once is mounted, debounced
  useEffect(() => {
    if (isMounted.current) {
      fetchSubscriptionsDebounced(params);
    } else {
      isMounted.current = true;
    }
  }, [fetchSubscriptionsDebounced, params]);

  // Run once on mount, no debounce
  useEffect(() => {
    fetchSubscriptions(params);
  }, []);

  useEffect(() => {
    dispatch(SubscriptionsActions.resetSubscription());
  }, [dispatch]);

  const handleParams = (params) => {
    const data = { ...params };
    // remove empty fields
    Object.keys(data).forEach((key) => !data[key] && delete data[key]);
    setParams(data);
    const _params = toQueryString(data);
    history.replace(buildUrl(`/subscriptions${_params}`));
  };

  const handlePageChange = (page) => {
    const newParams = { page: page, limit: params.limit };
    handleParams({ ...params, ...newParams });
  };

  // sort filter
  const handleSort = ({ column, sortDirection }) => {
    const newParams = {
      ...params,
      sort_field: column.selector,
      sort_desc: sortDirection === 'desc' ? true : false,
    };
    const _params = toQueryString(newParams);
    handleParams(_params);
  };

  // change number of items per page
  const handlePerRowsChange = async (limit) => {
    const newParams = { page: 1, limit: limit };
    handleParams({ ...params, ...newParams });
  };

  /**
   * Handle row click
   * @param {object} row Data of the row selected
   */
  const handleRowClick = (row) => {
    history.push(buildUrl(`/subscriptions/${row?.id}`));
  };
  // search by status, keyword and organization
  const search = (name, value) => {
    const filters = {
      ...params,
      page: 1,
      // validate if value is an object
      [name]: value,
    };
    // remove empty fields
    Object.keys(filters).forEach(
      (key) => !filters[key] && filters[key] !== false && delete filters[key]
    );
    handleParams(filters);
  };

  const DatetimeField = ({ datetime, tz = moment.tz.guess() }) => {
    return (
      <span>
        <p>{moment(datetime).tz(tz).format(`${localeFormat} hh:mm A`)}</p>
        <p className={classes.timezone}>{moment().tz(tz).format('UTC Z')}</p>
      </span>
    );
  };

  return (
    <Container className={classes.container}>
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Grid container spacing={2} direction="row" alignItems="center" justifyContent="space-between">
            <Grid item>
              <Typography variant="h6" gutterBottom>
                <Box fontWeight="fontWeightBold" data-cy="title" fontSize={24} my={1}>
                  {i18n.t('subscriptions.title')}
                </Box>
              </Typography>
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12} style={{ paddingBottom: '25px' }}>
          <Grid container spacing={2} direction="row" justifyContent="flex-start" alignItems="flex-end">
            <Grid item xs={12} sm={4} md={3}>
              <UsersSelect
                form={{
                  setFieldValue: search,
                }}
                type="users"
                margin="none"
                field={{
                  name: 'user_id',
                  value: params.user_id || '',
                }}
                label={i18n.t('common.filter_by_customer')}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <VehiclesSelect
                form={{
                  setFieldValue: search,
                }}
                field={{
                  name: 'vehicle_id',
                  value: params.vehicle_id || '',
                }}
                label={i18n.t('common.vehicle')}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <SubscriptionStatusSelect
                form={{
                  setFieldValue: search,
                }}
                margin="none"
                field={{
                  name: 'statuses',
                  value: params.statuses || '',
                }}
                label={i18n.t('common.status')}
              />
            </Grid>
            <Grid item xs={12} sm={4} md={3}>
              <StatusDetailsSelect
                form={{
                  setFieldValue: search,
                }}
                margin="none"
                field={{
                  name: 'status_detail',
                  value: params.status_detail || '',
                }}
                omitFields={['subscriptions', 'all']}
                label={i18n.t('common.status_details')}
              />
            </Grid>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Paper elevation={2} className={classes.paper}>
            <Table
              data={subscriptions}
              columns={columns}
              extraStyles={{ ...tableStyles }}
              loading={loading}
              onSort={handleSort}
              page={params.page}
              limit={params.limit}
              handleRowClicked={handleRowClick}
              handlePageChange={handlePageChange}
              totalRows={pagination?.totalRows || 0}
              handlePerRowsChange={handlePerRowsChange}
            />
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

SubscriptionList.propTypes = {
  history: PropTypes.any.isRequired,
};

export default SubscriptionList;
